import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, Search, X, ChevronDown, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Supported languages — single source of truth.  Native labels never
 * translate so a speaker can always identify their own language.
 */
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English",   flag: "🇺🇸" },
  { code: "es", label: "Español",   flag: "🇪🇸" },
  { code: "fr", label: "Français",  flag: "🇫🇷" },
  { code: "ar", label: "العربية",  flag: "🇸🇦" },
  { code: "ru", label: "Русский",  flag: "🇷🇺" },
  { code: "zh", label: "中文",       flag: "🇨🇳" },
  { code: "ja", label: "日本語",     flag: "🇯🇵" },
  { code: "ko", label: "한국어",     flag: "🇰🇷" },
];

/**
 * Indic languages — used ONLY for BOH recipe method translation via
 * Sarvam AI.  Picking one here does NOT change the UI language (no UI
 * translations exist for these); it surfaces a hint to use the recipe's
 * BOH Translate panel.  BCP-47 codes mirror the Sarvam API contract.
 */
export const INDIC_LANGUAGES = [
  { code: "hi-IN", label: "हिन्दी",     english: "Hindi" },
  { code: "ta-IN", label: "தமிழ்",       english: "Tamil" },
  { code: "te-IN", label: "తెలుగు",      english: "Telugu" },
  { code: "mr-IN", label: "मराठी",       english: "Marathi" },
  { code: "bn-IN", label: "বাংলা",       english: "Bengali" },
  { code: "gu-IN", label: "ગુજરાતી",     english: "Gujarati" },
  { code: "kn-IN", label: "ಕನ್ನಡ",       english: "Kannada" },
  { code: "ml-IN", label: "മലയാളം",     english: "Malayalam" },
  { code: "pa-IN", label: "ਪੰਜਾਬੀ",      english: "Punjabi" },
  { code: "od-IN", label: "ଓଡ଼ିଆ",        english: "Odia" },
];

/**
 * Searchable language switcher with max-height + overflow-y-auto.
 *
 * Props:
 *   variant   — "dark" (slate chrome, default) | "light" (cream / guest menu)
 *   onChange  — optional async callback fired after i18n.changeLanguage
 *   align     — "end" (default) | "start" | "center"
 *   compact   — hide the current-language code on mobile
 */
export default function GlobalLanguageSwitcher(props) {
  const variant = props.variant || "dark";
  const onChange = props.onChange;
  const align = props.align || "end";
  const compact = props.compact || false;
  // Guest Menu shows Indic languages as first-class FOH options (translated
  // via Sarvam server-side).  Admin nav keeps them nested as a BOH hint.
  const flattenIndic = props.flattenIndic || false;

  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [indicExpanded, setIndicExpanded] = useState(flattenIndic);
  const wrapRef = useRef(null);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ||
    (() => {
      // If the user has picked an Indic language, surface it in the trigger.
      const indic = INDIC_LANGUAGES.find((l) => l.code === i18n.language);
      if (indic) return { code: indic.code, label: indic.label, flag: "🇮🇳" };
      return SUPPORTED_LANGUAGES[0];
    })();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SUPPORTED_LANGUAGES;
    return SUPPORTED_LANGUAGES.filter(
      (l) =>
        l.code.toLowerCase().includes(q) ||
        l.label.toLowerCase().includes(q)
    );
  }, [query]);

  // When the user searches, also surface any Indic matches and auto-expand
  // the group so the result is visible without an extra click.
  const filteredIndic = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return INDIC_LANGUAGES;
    return INDIC_LANGUAGES.filter(
      (l) =>
        l.code.toLowerCase().includes(q) ||
        l.label.toLowerCase().includes(q) ||
        l.english.toLowerCase().includes(q)
    );
  }, [query]);

  useEffect(() => {
    if (query.trim() && filteredIndic.length > 0) setIndicExpanded(true);
  }, [query, filteredIndic.length]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const handlePick = async (code) => {
    if (code === i18n.language) {
      setOpen(false);
      return;
    }
    await i18n.changeLanguage(code);
    setOpen(false);
    setQuery("");
    if (onChange) {
      try {
        await onChange(code);
      } catch {
        /* caller handles */
      }
    }
  };

  // Indic pick — now a FIRST-CLASS UI language switch everywhere (page titles
  // are Sarvam-translated for all 10 scripts).  The old BOH-only toast hint
  // has been retired now that short-code resources (hi, ta, …) + their BCP-47
  // aliases (hi-IN, ta-IN, …) are both registered in i18n.
  const handleIndicPick = async (lang) => {
    if (lang.code === i18n.language) {
      setOpen(false);
      return;
    }
    await i18n.changeLanguage(lang.code);
    setOpen(false);
    setQuery("");
    if (onChange) {
      try { await onChange(lang.code); } catch { /* caller handles */ }
    }
  };

  const isLight = variant === "light";

  const triggerCls = isLight
    ? "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 hover:border-stone-400 transition-colors text-xs uppercase tracking-[0.22em]"
    : "flex items-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors";

  const panelPositionCls =
    align === "start" ? "left-0" : align === "center" ? "left-1/2 -translate-x-1/2" : "right-0";

  const panelCls = isLight
    ? cn(
        "absolute z-50 mt-2 min-w-[220px] rounded-md border border-stone-300 bg-[#faf7f2] shadow-lg overflow-hidden",
        panelPositionCls
      )
    : cn(
        "absolute z-50 mt-2 min-w-[220px] rounded-md border border-slate-700 bg-slate-900 shadow-lg overflow-hidden",
        panelPositionCls
      );

  const headerCls = isLight
    ? "px-3 pt-3 pb-1 text-[0.6rem] uppercase tracking-[0.22em] text-stone-500 font-semibold"
    : "px-3 pt-3 pb-1 text-[0.6rem] uppercase tracking-[0.22em] text-amber-400 font-semibold";

  const searchBoxCls = isLight
    ? "flex items-center gap-2 px-3 py-2 border-b border-stone-200 bg-stone-50"
    : "flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-slate-950";

  const searchInputCls = isLight
    ? "flex-1 bg-transparent text-sm text-stone-900 placeholder:text-stone-400 outline-none"
    : "flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none";

  const mutedCls = isLight ? "text-stone-500" : "text-slate-400";

  const rowBase =
    "w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors cursor-pointer";

  const rowHoverCls = isLight ? rowBase + " hover:bg-stone-100" : rowBase + " hover:bg-slate-800";

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        className={triggerCls}
        title={t("languageSwitcher.tooltip")}
        aria-label={t("languageSwitcher.tooltip")}
        onClick={() => setOpen((v) => !v)}
        data-testid="global-language-switcher-trigger"
      >
        <Globe className="h-4 w-4" />
        <span className="text-base leading-none">{current.flag}</span>
        <span
          className={cn(
            "text-sm font-medium uppercase",
            compact && "hidden sm:inline"
          )}
        >
          {current.code}
        </span>
      </button>

      {open && (
        <div className={panelCls} data-testid="global-language-switcher-menu" role="listbox">
          <div className={headerCls}>{t("languageSwitcher.chooseLanguage")}</div>
          <div className={searchBoxCls}>
            <Search className={cn("h-4 w-4 shrink-0", mutedCls)} />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("languageSwitcher.tooltip")}
              className={searchInputCls}
              data-testid="global-language-switcher-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className={mutedCls}
                aria-label="Clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="max-h-[320px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className={cn("px-3 py-6 text-center text-sm", mutedCls)}>
                {t("currencyPicker.empty")}
              </div>
            ) : (
              filtered.map((lang) => {
                const active = lang.code === i18n.language;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handlePick(lang.code)}
                    className={cn(
                      rowHoverCls,
                      active && (isLight ? "text-stone-900 font-semibold bg-stone-50" : "text-amber-400 font-semibold bg-slate-800/50")
                    )}
                    data-testid={"global-language-switcher-option-" + lang.code}
                    role="option"
                    aria-selected={active}
                  >
                    <span className="text-lg leading-none">{lang.flag}</span>
                    <span className={cn("flex-1", isLight ? "text-stone-800" : "text-white")}>
                      {lang.label}
                    </span>
                    <span className={cn("text-[0.65rem] uppercase tracking-[0.18em]", mutedCls)}>
                      {lang.code}
                    </span>
                    {active && <Check className="h-3.5 w-3.5" />}
                  </button>
                );
              })
            )}

            {/* ============== INDIC GROUP — Sarvam BOH translations ============== */}
            {filteredIndic.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setIndicExpanded((v) => !v)}
                  className={cn(
                    rowHoverCls,
                    "border-t",
                    isLight ? "border-stone-200" : "border-slate-800"
                  )}
                  data-testid="global-language-switcher-indic-toggle"
                  aria-expanded={indicExpanded}
                >
                  <span className="text-lg leading-none">🇮🇳</span>
                  <span className={cn("flex-1 flex flex-col text-left", isLight ? "text-stone-800" : "text-white")}>
                    <span className="font-medium">
                      {t("languageSwitcher.indicGroupLabel", {
                        defaultValue: "India (Indic Languages)",
                      })}
                    </span>
                    <span className={cn("text-[0.65rem]", mutedCls)}>
                      <ChefHat className="inline h-3 w-3 mr-1" />
                      {flattenIndic
                        ? t("languageSwitcher.indicGroupSubtitleFoh", {
                            defaultValue: "Native-script menu",
                          })
                        : t("languageSwitcher.indicGroupSubtitle", {
                            defaultValue: "10 scripts · native translations",
                          })}
                    </span>
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform",
                      indicExpanded && "rotate-180"
                    )}
                  />
                </button>

                {indicExpanded && (
                  <div
                    className={cn(
                      "pl-3 border-l-2 ml-3 my-1",
                      isLight ? "border-amber-400/40" : "border-amber-500/40"
                    )}
                    data-testid="global-language-switcher-indic-list"
                  >
                    {filteredIndic.map((lang) => {
                      const active = lang.code === i18n.language;
                      return (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => handleIndicPick(lang)}
                          className={cn(
                            rowHoverCls,
                            "text-xs py-1.5",
                            active && (isLight ? "text-stone-900 font-semibold bg-stone-50" : "text-amber-400 font-semibold bg-slate-800/50")
                          )}
                          data-testid={"global-language-switcher-indic-" + lang.code}
                          role="option"
                          aria-selected={active}
                        >
                          <span className={cn("flex-1 flex items-baseline gap-2", isLight ? "text-stone-800" : "text-white")}>
                            <span className="text-sm">{lang.label}</span>
                            <span className={cn("text-[0.65rem]", mutedCls)}>
                              {lang.english}
                            </span>
                          </span>
                          <span className={cn("text-[0.6rem] uppercase tracking-[0.15em] font-mono", mutedCls)}>
                            {lang.code}
                          </span>
                          {active && <Check className="h-3.5 w-3.5" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
